import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, 
  Edit, 
  Search,
  Briefcase,
  Mail,
  Phone,
  Calendar,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  User
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import kadryService from '../services/kadryService';

const formatDate = (d) => d ? new Date(d).toLocaleDateString('pl-PL') : '-';

const Pracownicy = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPracownik, setSelectedPracownik] = useState(null);
  const [formUrlop, setFormUrlop] = useState({
    dniUrlopuRocznie: '',
    dniUrlopuPrzeniesione: '',
    dniUrlopuBilansWejsciowy: '',
    dniUrlopuKorekta: ''
  });
  const [expandedId, setExpandedId] = useState(null);

  // Po 30 września bieżącego roku w PL — używane do oznaczania zaległych wniosków
  const poTerminieZaleglosci = useMemo(() => {
    const now = new Date();
    const cutoff = new Date(now.getFullYear(), 8, 30, 23, 59, 59); // 8 = wrzesień (0-based)
    return now > cutoff;
  }, []);

  const { data: pracownicyData, isLoading, error } = useQuery(
    'kadry-pracownicy',
    kadryService.getPracownicy,
    { refetchOnWindowFocus: false, retry: 1 }
  );

  const pracownicy = pracownicyData?.pracownicy || [];

  const filteredPracownicy = pracownicy.filter(p => {
    const s = searchTerm.toLowerCase();
    const name = `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase();
    return (
      name.includes(s) ||
      (p.email || '').toLowerCase().includes(s) ||
      (p.position || '').toLowerCase().includes(s) ||
      (p.department || '').toLowerCase().includes(s)
    );
  });

  const updateUrlopMutation = useMutation(
    ({ userId, payload }) => kadryService.updatePracownikUrlop(userId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('kadry-pracownicy');
        toast.success('Dane urlopowe zaktualizowane');
        setShowEditModal(false);
        setSelectedPracownik(null);
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Błąd aktualizacji')
    }
  );

  const openEditModal = (p) => {
    setSelectedPracownik(p);
    setFormUrlop({
      dniUrlopuRocznie: String(p.dniUrlopuRocznie ?? 26),
      dniUrlopuPrzeniesione: String(p.dniUrlopuPrzeniesione ?? 0),
      dniUrlopuBilansWejsciowy: String(p.dniUrlopuBilansWejsciowy ?? 0),
      dniUrlopuKorekta: String(p.dniUrlopuKorekta ?? 0)
    });
    setShowEditModal(true);
  };

  const handleSaveUrlop = (e) => {
    e.preventDefault();
    const wymiar = parseInt(formUrlop.dniUrlopuRocznie, 10);
    const przeniesione = parseInt(formUrlop.dniUrlopuPrzeniesione, 10);
    const bilans = parseInt(formUrlop.dniUrlopuBilansWejsciowy, 10);
    const korekta = parseInt(formUrlop.dniUrlopuKorekta, 10);

    if (isNaN(wymiar) || wymiar < 0) return toast.error('Wymiar urlopu rocznie musi być liczbą ≥ 0');
    if (isNaN(przeniesione) || przeniesione < 0) return toast.error('Dni przeniesione muszą być liczbą ≥ 0');
    if (isNaN(bilans) || bilans < 0) return toast.error('Bilans wejściowy musi być liczbą ≥ 0');
    if (isNaN(korekta)) return toast.error('Korekta ręczna musi być liczbą całkowitą');

    updateUrlopMutation.mutate({
      userId: selectedPracownik._id,
      payload: {
        dniUrlopuRocznie: wymiar,
        dniUrlopuPrzeniesione: przeniesione,
        dniUrlopuBilansWejsciowy: bilans,
        dniUrlopuKorekta: korekta
      }
    });
  };

  return (
    <div className="space-y-6">
      {(user?.role === 'admin' || user?.role === 'super admin') && (
        <div className="flex justify-end">
          <Link
            to="/users"
            className="btn-primary flex items-center space-x-2"
          >
            <User className="h-5 w-5" />
            <span>Zarządzaj użytkownikami</span>
          </Link>
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Szukaj (imię, nazwisko, email, stanowisko, dział)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-16 w-16 text-red-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Błąd ładowania</h3>
            <p className="text-gray-500">{error.response?.data?.message || 'Nie udało się załadować listy'}</p>
          </div>
        ) : filteredPracownicy.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'Nie znaleziono pracowników' : 'Brak pracowników'}
            </h3>
            <p className="text-gray-500">
              {searchTerm ? 'Zmień kryteria wyszukiwania' : 'Dodaj użytkowników w zakładce Użytkownicy'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pracownik</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stanowisko / Dział</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kontakt</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Urlop – saldo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Historia urlopów</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Akcje</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPracownicy.map((p) => (
                  <React.Fragment key={p._id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-blue-600 font-medium">
                              {(p.firstName || p.username || '?').charAt(0)}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {p.firstName || p.lastName ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : p.username}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div><Briefcase className="h-4 w-4 inline mr-1" />{p.position || '-'}</div>
                        {p.department && <div className="text-xs text-gray-400 mt-0.5">{p.department}</div>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {p.email && <div className="flex items-center"><Mail className="h-4 w-4 mr-1" />{p.email}</div>}
                        {p.phone && <div className="flex items-center mt-1"><Phone className="h-4 w-4 mr-1" />{p.phone}</div>}
                        {!p.email && !p.phone && '-'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm space-y-0.5">
                          <div className="flex items-baseline space-x-1">
                            <span className="text-gray-500 text-xs">Wymiar:</span>
                            <span className="text-gray-700 font-medium">{p.dniUrlopuRocznie ?? 26}</span>
                            {(p.dniUrlopuPrzeniesione ?? 0) > 0 && (
                              <>
                                <span className="text-gray-400">+</span>
                                <span className="text-amber-700 font-medium" title="Dni zaległe z poprzedniego roku">
                                  {p.dniUrlopuPrzeniesione} zal.
                                </span>
                              </>
                            )}
                          </div>
                          <div className="flex items-baseline space-x-1">
                            <span className="text-gray-500 text-xs">Wykorzystano:</span>
                            <span className="text-orange-600">{p.dniWykorzystane ?? 0}</span>
                          </div>
                          <div className="flex items-baseline space-x-1">
                            <span className="text-gray-500 text-xs">Pozostało:</span>
                            <span className="text-green-600 font-semibold">{p.dniPozostale ?? 0}</span>
                          </div>
                          {(p.dniZaleglePozostale ?? 0) > 0 && (
                            <div
                              className={`flex items-center space-x-1 text-xs ${
                                poTerminieZaleglosci ? 'text-red-700 font-semibold' : 'text-amber-700'
                              }`}
                              title={poTerminieZaleglosci
                                ? 'Termin wykorzystania zaległego urlopu (30 września) minął'
                                : 'Zaległe dni z poprzedniego roku — wykorzystaj do 30 września'}
                            >
                              <AlertTriangle className="h-3.5 w-3.5" />
                              <span>
                                {p.dniZaleglePozostale} dni zal. {poTerminieZaleglosci ? 'po terminie' : 'do 30.09'}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {p.urlopy && p.urlopy.length > 0 ? (
                          <button
                            onClick={() => setExpandedId(expandedId === p._id ? null : p._id)}
                            className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
                          >
                            {expandedId === p._id ? <ChevronDown className="h-4 w-4 mr-1" /> : <ChevronRight className="h-4 w-4 mr-1" />}
                            {p.urlopy.length} urlop{p.urlopy.length === 1 ? '' : 'ów'}
                          </button>
                        ) : (
                          <span className="text-sm text-gray-400">Brak</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => openEditModal(p)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edytuj dni urlopu"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                    {expandedId === p._id && p.urlopy && p.urlopy.length > 0 && (
                      <tr>
                        <td colSpan={6} className="bg-gray-50 px-6 py-3">
                          <div className="text-sm">
                            <p className="font-medium text-gray-700 mb-2">Urlopy wypoczynkowe (ten rok):</p>
                            <ul className="space-y-1">
                              {p.urlopy.map((u, i) => (
                                <li key={i} className="flex items-center text-gray-600">
                                  <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                                  {formatDate(u.dataOd)} – {formatDate(u.dataDo)} ({u.liczbaDni} dni)
                                </li>
                              ))}
                            </ul>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showEditModal && selectedPracownik && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-full overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Saldo urlopu pracownika</h3>
              <button
                onClick={() => { setShowEditModal(false); setSelectedPracownik(null); }}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >×</button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {selectedPracownik.firstName || selectedPracownik.lastName
                ? `${selectedPracownik.firstName || ''} ${selectedPracownik.lastName || ''}`.trim()
                : selectedPracownik.username}
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-xs text-blue-900 space-y-1">
              <p>
                <strong>Wymiar rocznie</strong> — pełny przysługujący wymiar urlopu (ustawiasz ręcznie, np. 20 lub 26).
              </p>
              <p>
                <strong>Dni przeniesione z poprzedniego roku</strong> — niewykorzystany urlop, który przechodzi
                na bieżący rok. Zgodnie z KP musi być wykorzystany do 30 września.
              </p>
              <p>
                <strong>Bilans wejściowy</strong> — dni już wykorzystane PRZED dodaniem do systemu (np. nowy
                pracownik miał wzięte 7 dni). Ustawiasz raz przy dodawaniu.
              </p>
              <p>
                <strong>Korekta ręczna</strong> — np. urlopy wzięte poza systemem; może być ujemna.
              </p>
            </div>

            <form onSubmit={handleSaveUrlop} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Wymiar urlopu rocznie</label>
                <input
                  type="number"
                  min="0"
                  value={formUrlop.dniUrlopuRocznie}
                  onChange={(e) => setFormUrlop(s => ({ ...s, dniUrlopuRocznie: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dni przeniesione z poprzedniego roku <span className="text-amber-700">(zaległe)</span>
                </label>
                <input
                  type="number"
                  min="0"
                  value={formUrlop.dniUrlopuPrzeniesione}
                  onChange={(e) => setFormUrlop(s => ({ ...s, dniUrlopuPrzeniesione: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Wpisuj 1 stycznia po podsumowaniu poprzedniego roku.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bilans wejściowy (dni już wykorzystane przed dodaniem)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formUrlop.dniUrlopuBilansWejsciowy}
                  onChange={(e) => setFormUrlop(s => ({ ...s, dniUrlopuBilansWejsciowy: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Korekta ręczna (może być ujemna)
                </label>
                <input
                  type="number"
                  step="1"
                  value={formUrlop.dniUrlopuKorekta}
                  onChange={(e) => setFormUrlop(s => ({ ...s, dniUrlopuKorekta: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Podgląd na żywo - obliczenia z aktualnego stanu pracownika */}
              <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-700 space-y-1">
                <p className="font-medium text-gray-800 mb-1">W systemie (obecny stan):</p>
                <p>Z zatwierdzonych wniosków: <strong>{selectedPracownik.dniWykorzystaneZWnioskow ?? 0}</strong> dni</p>
                <p>Łącznie wykorzystane (z bilansem i korektą): <strong>{selectedPracownik.dniWykorzystane ?? 0}</strong></p>
                <p>Dostępne (wymiar + przeniesione): <strong>{selectedPracownik.dniDostepneOgolem ?? 0}</strong></p>
                <p className="text-green-700">Pozostało: <strong>{selectedPracownik.dniPozostale ?? 0}</strong></p>
                {(selectedPracownik.dniZaleglePozostale ?? 0) > 0 && (
                  <p className={poTerminieZaleglosci ? 'text-red-700' : 'text-amber-700'}>
                    Zaległe pozostałe: <strong>{selectedPracownik.dniZaleglePozostale}</strong> dni
                    {poTerminieZaleglosci ? ' (po 30.09 — termin minął)' : ' (do 30.09)'}
                  </p>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setSelectedPracownik(null); }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={updateUrlopMutation.isLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {updateUrlopMutation.isLoading ? 'Zapisywanie...' : 'Zapisz'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pracownicy;
